import { useMapsLibrary } from '@vis.gl/react-google-maps';
import { useCallback, useEffect, useState } from 'react';

type StreetViewStatus = 'LOADING' | 'OK' | 'NOT_AVAILABLE' | 'ERROR';

type StreetViewResult = {
  panoramaPosition: google.maps.LatLngLiteral | null;
  status: StreetViewStatus;
  heading: number;
};

export const useStreetView = (
  targetPosition: google.maps.LatLngLiteral | null,
): StreetViewResult => {
  const streetViewLib = useMapsLibrary('streetView');
  const [panoramaPosition, setPanoramaPosition] = useState<google.maps.LatLngLiteral | null>(null);
  const [status, setStatus] = useState<StreetViewStatus>('LOADING');

  const calculateBearing = useCallback(
    (from: google.maps.LatLngLiteral, to: google.maps.LatLngLiteral): number => {
      const lat1 = (from.lat * Math.PI) / 180;
      const lat2 = (to.lat * Math.PI) / 180;
      const deltaLng = ((to.lng - from.lng) * Math.PI) / 180;

      const x = Math.sin(deltaLng) * Math.cos(lat2);
      const y =
        Math.cos(lat1) * Math.sin(lat2) - Math.sin(lat1) * Math.cos(lat2) * Math.cos(deltaLng);

      const bearing = Math.atan2(x, y);

      return ((bearing * 180) / Math.PI + 360) % 360;
    },
    [],
  );

  const searchForPanorama = useCallback(
    async (position: google.maps.LatLngLiteral) => {
      if (!streetViewLib) return;

      setStatus('LOADING');

      try {
        const service = new streetViewLib.StreetViewService();

        const strategies = [
          // Start with very close proximity for best building view
          {
            source: google.maps.StreetViewSource.OUTDOOR,
            preference: google.maps.StreetViewPreference.BEST,
            radius: 30,
          },
          // Try slightly wider radius with best preference (prioritizes quality)
          {
            source: google.maps.StreetViewSource.OUTDOOR,
            preference: google.maps.StreetViewPreference.BEST,
            radius: 60,
          },
          // Nearest within small radius (prioritizes proximity)
          {
            source: google.maps.StreetViewSource.OUTDOOR,
            preference: google.maps.StreetViewPreference.NEAREST,
            radius: 75,
          },
          // Best quality within moderate range
          {
            source: google.maps.StreetViewSource.OUTDOOR,
            preference: google.maps.StreetViewPreference.BEST,
            radius: 150,
          },
          // Default source for edge cases
          {
            source: google.maps.StreetViewSource.DEFAULT,
            preference: google.maps.StreetViewPreference.BEST,
            radius: 100,
          },
          // Fallback with wider range if nothing else works
          {
            source: google.maps.StreetViewSource.OUTDOOR,
            preference: google.maps.StreetViewPreference.NEAREST,
            radius: 300,
          },
        ];

        for (const strategy of strategies) {
          const result = await new Promise<google.maps.LatLngLiteral | null>(resolve => {
            void service.getPanorama(
              {
                location: position,
                preference: strategy.preference,
                radius: strategy.radius,
                source: strategy.source,
              },
              (panoData, streetViewStatus) => {
                if (
                  streetViewStatus === google.maps.StreetViewStatus.OK &&
                  panoData?.location?.latLng
                ) {
                  const panoLocation = panoData.location.latLng;
                  resolve({
                    lat: panoLocation.lat(),
                    lng: panoLocation.lng(),
                  });
                } else {
                  resolve(null);
                }
              },
            );
          });

          if (result) {
            setPanoramaPosition(result);
            setStatus('OK');

            return;
          }
        }

        setStatus('NOT_AVAILABLE');
      } catch (error) {
        setStatus('ERROR');
      }
    },
    [streetViewLib],
  );

  useEffect(() => {
    if (!streetViewLib || !targetPosition) {
      return;
    }

    void searchForPanorama(targetPosition);
  }, [streetViewLib, targetPosition, searchForPanorama]);

  const heading =
    panoramaPosition && targetPosition ? calculateBearing(panoramaPosition, targetPosition) : 0;

  return {
    panoramaPosition,
    status,
    heading,
  };
};
